import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import type { ParametroResponse } from '@core/models/api/parametro.model';

interface CacheEntry {
  expiresAt: number;
  stream$: Observable<ParametroResponse | null>;
}

const CACHE_TTL_MS = 5 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class ParametroService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/api/parametros`;
  private readonly cache = new Map<string, CacheEntry>();

  /**
   * Get a system parameter by its unique key.
   * Returns null when the parameter does not exist (404).
   * Network/5xx errors are surfaced via the observable error channel.
   *
   * Note: `valor` is always a serialized string. Callers must parse to
   * the desired type (number, boolean, JSON) at the call site.
   */
  getParametro(clave: string): Observable<ParametroResponse | null> {
    const cached = this.cache.get(clave);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.stream$;
    }

    const stream$ = this.http
      .get<ParametroResponse>(`${this.baseUrl}/${encodeURIComponent(clave)}`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(null);
          }
          this.cache.delete(clave);
          return throwError(() => this.toFriendlyError(error));
        }),
        tap({
          error: () => this.cache.delete(clave),
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.cache.set(clave, {
      expiresAt: Date.now() + CACHE_TTL_MS,
      stream$,
    });

    return stream$;
  }

  /**
   * Manually invalidate the cache for a specific key, or the entire cache.
   */
  invalidate(clave?: string): void {
    if (clave) {
      this.cache.delete(clave);
    } else {
      this.cache.clear();
    }
  }

  private toFriendlyError(error: HttpErrorResponse): { status: number; message: string } {
    let message = 'Ocurrió un error inesperado. Por favor, intente nuevamente.';

    if (error.error instanceof ErrorEvent) {
      message = 'Error de conexión. Verifique su conexión a internet.';
    } else if (error.status >= 500) {
      message = 'Error del servidor. Por favor, intente más tarde.';
    }

    return { status: error.status, message };
  }
}
